/**
 * Clients and sites: every project this device or the account's workspaces know, grouped by
 * client and installation address, with the last inspection and when the next is due
 * (@elec/domain sites.ts). Reminders list what is overdue or due within six months; they can go
 * to a calendar as an .ics file.
 */
import { useEffect, useMemo, useState } from 'react';
import { groupClients, normaliseKey, reminders, remindersIcs, type InspectionDue } from '@elec/domain';
import type { Locale, Translate } from '@elec/i18n';
import { localeTag } from '@elec/i18n';
import type { CloudManager } from './cloud.ts';
import { forgetSite, mergeSites, readSiteIndex, type IndexedSite } from './siteIndex.ts';
import { Dialog } from './ui.tsx';

const today = () => new Date().toISOString().slice(0, 10);

export function ClientsDialog(props: {
  t: Translate;
  locale: Locale;
  cloud: CloudManager;
  /** The open project (its own summary is the freshest). */
  currentId: string;
  openCloud(id: string): void;
  saveFile(bytes: Uint8Array, name: string): void;
  onClose(): void;
}) {
  const { t, locale } = props;
  const [device, setDevice] = useState(readSiteIndex);
  const [remote, setRemote] = useState<IndexedSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const me = props.cloud.state.me;
  useEffect(() => {
    const client = props.cloud.client;
    if (!me || !client) return;
    setLoading(true);
    Promise.all(
      me.teams.map((team) =>
        client
          .teamSites(team.id)
          .then((list) => list.map((s): IndexedSite => ({ ...s, where: 'cloud', teamId: team.id }))),
      ),
    )
      .then((lists) => setRemote(lists.flat()))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [me?.user.id]);
  const all = useMemo(() => mergeSites(device, remote), [device, remote]);
  const clients = useMemo(() => {
    const k = normaliseKey(q);
    const list = groupClients(all);
    if (!k) return list;
    return list.filter((c) =>
      normaliseKey(
        [
          c.name,
          c.address,
          c.email,
          c.phone,
          ...c.sites.flatMap((s) => [s.address, ...s.projects.map((p) => p.title)]),
        ].join(' '),
      ).includes(k),
    );
  }, [all, q]);
  const due = useMemo(() => reminders(groupClients(all), today()), [all]);
  const byId = useMemo(() => new Map(all.map((s) => [s.projectId, s])), [all]);
  const date = (iso: string) => new Date(iso).toLocaleDateString(localeTag(locale), { dateStyle: 'medium' });
  const dueText = (d: InspectionDue) =>
    t(d.kind === 'periodic' ? 'clients.duePeriodic' : 'clients.dueReinspection', { date: date(d.date) });
  const exportIcs = () => {
    const items = reminders(groupClients(all), today(), 366 * 30).map((r) => ({
      due: r.due,
      uid: `${normaliseKey(r.client.name || r.site.address).replace(/ /g, '-')}-${r.due.date}`,
      title: `${t(r.due.kind === 'periodic' ? 'clients.icsPeriodic' : 'clients.icsReinspection')} — ${r.client.name || r.site.address}`,
      description: [r.site.address, r.client.phone, r.client.email, ...r.site.projects.map((p) => p.title)]
        .filter(Boolean)
        .join('\n'),
    }));
    props.saveFile(new TextEncoder().encode(remindersIcs(items)), `${t('clients.icsFile')}.ics`);
  };
  const open = (s: IndexedSite) => (s.where === 'cloud' ? props.openCloud(s.projectId) : undefined);
  return (
    <Dialog
      title={t('clients.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <span className="muted small grow">
            {loading
              ? t('clients.loading')
              : error
                ? t('clients.cloudError')
                : t('clients.count', { n: clients.length })}
          </span>
          <button onClick={exportIcs} disabled={!all.some((s) => s.due)} data-testid="clients-ics">
            {t('clients.ics')}
          </button>
          <button className="primary" onClick={props.onClose}>
            {t('dialog.close')}
          </button>
        </>
      }
    >
      <div className="clients">
        {due.length > 0 && (
          <section className="clients-due" data-testid="clients-due">
            <h3>{t('clients.remindersTitle')}</h3>
            <ul>
              {due.map((r) => (
                <li key={`${r.client.key}/${r.site.key}`} className={r.inDays < 0 ? 'overdue' : ''}>
                  <strong>{r.client.name || r.site.address}</strong>
                  {r.client.name && r.site.address ? ` · ${r.site.address}` : ''}
                  <span className="muted">
                    {' — '}
                    {dueText(r.due)}{' '}
                    {r.inDays < 0
                      ? t('clients.overdue', { n: -r.inDays })
                      : t('clients.inDays', { n: r.inDays })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <input
          type="search"
          className="clients-search"
          placeholder={t('clients.search')}
          aria-label={t('clients.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="clients-search"
        />
        {clients.length === 0 ? (
          <p className="muted">{all.length ? t('clients.noMatch') : t('clients.empty')}</p>
        ) : (
          <ul className="clients-list" data-testid="clients-list">
            {clients.map((c) => (
              <li key={c.key}>
                <div className="client-head">
                  <strong>{c.name || t('clients.noName')}</strong>
                  <span className="muted small">
                    {[c.address, c.phone, c.email].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {c.sites.map((site) => (
                  <div key={site.key} className="client-site">
                    <div>
                      <span>{site.address || t('clients.noAddress')}</span>
                      {site.due && <span className="badge">{dueText(site.due)}</span>}
                    </div>
                    <ul>
                      {site.projects.map((p) => {
                        const s = byId.get(p.projectId) ?? (p as IndexedSite);
                        return (
                          <li key={p.projectId}>
                            <span>{p.title || t('clients.untitled')}</span>
                            <span className="muted small">
                              {p.lastInspection
                                ? t('clients.lastInspection', {
                                    date: date(p.lastInspection.date),
                                    result: t(`clients.result.${p.lastInspection.result}`),
                                  })
                                : t('clients.noInspection')}
                              {p.openRemarks ? ` · ${t('clients.openRemarks', { n: p.openRemarks })}` : ''}
                              {' · '}
                              {p.projectId === props.currentId
                                ? t('clients.isOpen')
                                : s.where === 'cloud'
                                  ? t('clients.inCloud')
                                  : s.file
                                    ? t('clients.onDeviceFile', { file: s.file })
                                    : t('clients.onDevice')}
                            </span>
                            {s.where === 'cloud' && p.projectId !== props.currentId && (
                              <button className="link small" onClick={() => open(s)}>
                                {t('clients.open')}
                              </button>
                            )}
                            {s.where === 'device' && (
                              <button
                                className="link small"
                                onClick={() => {
                                  forgetSite(p.projectId);
                                  setDevice(readSiteIndex());
                                }}
                              >
                                {t('clients.forget')}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">{t('clients.hint')}</p>
      </div>
    </Dialog>
  );
}
